import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Store, Truck, Music, CheckCircle, Sparkles, Building2, Megaphone, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const API = "/api";

const SubmitBusiness = () => {
  const [submitted, setSubmitted] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    category: '',
    description: '',
    website: '',
    social_media: '',
    why_feature: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/feature-requests`, form);
      setRequestId(response.data.id);
      setSubmitted(true);
      toast.success('Request submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'artist': return Music;
      case 'food_truck': return Truck;
      case 'vendor': return Store;
      default: return Sparkles;
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="submit-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md space-y-6"
        >
          <div className="w-20 h-20 mx-auto rounded-full bg-secondary/20 flex items-center justify-center">
            <CheckCircle size={40} className="text-secondary" />
          </div>
          <h1 className="font-heading text-3xl font-bold">Request Submitted!</h1>
          <p className="text-muted-foreground">
            Thank you for your interest in being featured on Twin Tube! We'll review your submission and get back to you soon.
          </p>
          <div className="p-4 bg-card rounded-sm border border-border">
            <p className="text-sm text-muted-foreground mb-1">Your Reference ID:</p>
            <code className="text-primary font-mono">{requestId}</code>
          </div>
          <p className="text-sm text-muted-foreground">
            Save this ID to check your request status later.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="submit-business-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4"
      >
        <div className="flex justify-center gap-4">
          <div className="p-3 rounded-full bg-primary/20">
            <Music size={24} className="text-primary" />
          </div>
          <div className="p-3 rounded-full bg-secondary/20">
            <Truck size={24} className="text-secondary" />
          </div>
          <div className="p-3 rounded-full bg-primary/20">
            <Store size={24} className="text-primary" />
          </div>
        </div>
        
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
          Get Featured on <span className="text-primary">Twin</span><span className="text-secondary">Tube</span>
        </h1>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Are you a local artist, food truck, or artisan vendor in the Twin Cities? 
          Submit your business to be featured on our platform and reach thousands of local music lovers!
        </p>
      </motion.div>

      {/* Quick Links for Special Registrations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      >
        <Link to="/register/band">
          <Card className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer h-full">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-primary/20">
                <Music size={18} className="text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">Register Artist</p>
                <p className="text-xs text-muted-foreground">Musicians, bands & comedians</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/register/venue">
          <Card className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer h-full">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-500/20">
                <Building2 size={18} className="text-blue-400" />
              </div>
              <div>
                <p className="font-medium text-sm">Register Venue</p>
                <p className="text-xs text-muted-foreground">Clubs & concert halls</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/register/promoter">
          <Card className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer h-full">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-orange-500/20">
                <Megaphone size={18} className="text-orange-400" />
              </div>
              <div>
                <p className="font-medium text-sm">Register Promoter</p>
                <p className="text-xs text-muted-foreground">Event promoters</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/register/content-creator">
          <Card className="bg-card border-border hover:border-pink-500/50 transition-colors cursor-pointer h-full">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-gradient-to-br from-pink-500/20 to-purple-500/20">
                <Video size={18} className="text-pink-400" />
              </div>
              <div>
                <p className="font-medium text-sm">Content Creator</p>
                <p className="text-xs text-muted-foreground">Video, photo, podcast</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </motion.div>

      {/* Benefits */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles size={18} className="text-secondary" />
              Featured Spotlight
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Get featured on our homepage "Featured This Week" section
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Music size={18} className="text-primary" />
              Local Audience
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Connect with Twin Cities music fans and local supporters
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Store size={18} className="text-secondary" />
              Free Promotion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              No cost to apply - support local Minneapolis & St. Paul businesses
            </p>
          </CardContent>
        </Card>
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Submit Your Business</CardTitle>
            <CardDescription>Fill out the form below and we'll review your submission</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category */}
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={(v) => updateForm('category', v)} required>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select your category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="artist">
                      <div className="flex items-center gap-2">
                        <Music size={16} /> Artist / Band
                      </div>
                    </SelectItem>
                    <SelectItem value="food_truck">
                      <div className="flex items-center gap-2">
                        <Truck size={16} /> Food Truck
                      </div>
                    </SelectItem>
                    <SelectItem value="vendor">
                      <div className="flex items-center gap-2">
                        <Store size={16} /> Artisan Vendor
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Business Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business / Artist Name *</Label>
                  <Input
                    id="business_name"
                    value={form.business_name}
                    onChange={(e) => updateForm('business_name', e.target.value)}
                    placeholder="Your business or artist name"
                    required
                    data-testid="business-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    value={form.contact_name}
                    onChange={(e) => updateForm('contact_name', e.target.value)}
                    placeholder="Your full name"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => updateForm('email', e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => updateForm('phone', e.target.value)}
                    placeholder="(612) 555-0123"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              {/* Online Presence */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="website">Website (optional)</Label>
                  <Input
                    id="website"
                    value={form.website}
                    onChange={(e) => updateForm('website', e.target.value)}
                    placeholder="https://yourbusiness.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="social_media">Social Media (optional)</Label>
                  <Input
                    id="social_media"
                    value={form.social_media}
                    onChange={(e) => updateForm('social_media', e.target.value)}
                    placeholder="@yourbusiness"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Tell us about your business *</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  placeholder="Describe what you do, your history in the Twin Cities, what makes you unique..."
                  rows={3}
                  required
                  data-testid="description-input"
                />
              </div>

              {/* Why Feature */}
              <div className="space-y-2">
                <Label htmlFor="why_feature">Why should we feature you? *</Label>
                <Textarea
                  id="why_feature"
                  value={form.why_feature}
                  onChange={(e) => updateForm('why_feature', e.target.value)}
                  placeholder="Tell us why you'd be a great fit for Twin Tube's featured section..."
                  rows={3}
                  required
                  data-testid="why-feature-input"
                />
              </div>

              {/* Submit */}
              <Button 
                type="submit" 
                size="lg" 
                className="w-full gap-2"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <>Submitting...</>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Request
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                By submitting, you agree to be contacted about your feature request. 
                We typically review submissions within 3-5 business days.
              </p>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default SubmitBusiness;
